import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { API } from '../../app/ApiURL';
const BOOK_DEMO_API_URL = API.BOOK_DEMO;

// Brand gradient
const GRADIENT = 'linear-gradient(144.01deg, #ffc951 0%, #c94daa 100%)';
const gradientStyle = { background: GRADIENT };

const initialForm = {
  name: '',
  phone: '',
  email: '',
  preferred_date: '',
  preferred_time: '',
};

const pad = (n) => String(n).padStart(2, '0');

// Today as YYYY-MM-DD (local time)
const getToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Current time as HH:MM (local time)
const getNowTime = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const NAME_REGEX = /^[A-Za-z]+(?: [A-Za-z]+)*$/; // letters and single spaces only
const PHONE_REGEX = /^\d{10}$/; // exactly 10 digits
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

const validate = (f) => {
  const errors = {};
  const name = f.name.trim();
  const email = f.email.trim();
  const today = getToday();

  if (!name) errors.name = 'Name is required';
  else if (name.length < 2) errors.name = 'Name must be at least 2 letters';
  else if (name.length > 200) errors.name = 'Name must be 200 characters or less';
  else if (!NAME_REGEX.test(name)) errors.name = 'Name can contain letters only';

  if (!f.phone) errors.phone = 'Phone number is required';
  else if (!PHONE_REGEX.test(f.phone)) errors.phone = 'Enter a valid 10-digit phone number';

  if (!email) errors.email = 'Email is required';
  else if (!EMAIL_REGEX.test(email)) errors.email = 'Enter a valid email (e.g. you@example.com)';

  if (!f.preferred_date) errors.preferred_date = 'Select a date';
  else if (f.preferred_date < today) errors.preferred_date = 'Past dates are not allowed';

  if (!f.preferred_time) errors.preferred_time = 'Select a time';
  else if (f.preferred_date === today && f.preferred_time <= getNowTime())
    errors.preferred_time = 'Past time is not allowed';

  return errors;
};

const inputClass = (hasError) =>
  `w-full rounded-lg border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 ${
    hasError
      ? 'border-red-400 focus:ring-red-200'
      : 'border-gray-300 focus:border-[#c94daa] focus:ring-[#c94daa]/25'
  }`;

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const DemoModal = ({ onClose }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Close on Escape + lock background scroll while open
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Block invalid characters while typing
    if (name === 'name') value = value.replace(/[^A-Za-z ]/g, '').replace(/ {2,}/g, ' ');
    if (name === 'phone') value = value.replace(/\D/g, '').slice(0, 10);

    setForm((prev) => ({ ...prev, [name]: value }));

    // Clear this field's error, and the time error when the date changes
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
      ...(name === 'preferred_date' ? { preferred_time: undefined } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    try {
      const res = await fetch(BOOK_DEMO_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone,
          email: form.email.trim(),
          preferred_date: form.preferred_date,
          preferred_time: form.preferred_time,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 201) {
        setSuccessMsg(
          data.message ||
            'Demo booking submitted successfully. We will get in touch with you shortly.'
        );
        setForm(initialForm);
        return;
      }

      // 400 validation errors: map field errors if the backend sends them
      if (data.errors && typeof data.errors === 'object') {
        const mapped = {};
        Object.entries(data.errors).forEach(([key, val]) => {
          mapped[key] = Array.isArray(val) ? val[0] : String(val);
        });
        setErrors(mapped);
      }
      setServerError(data.message || 'Something went wrong. Please check your details.');
    } catch (err) {
      setServerError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const today = getToday();
  // Only restrict the time picker when today's date is selected
  const minTime = form.preferred_date === today ? getNowTime() : undefined;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-modal-title"
    >
      <div
        className="relative w-full max-w-[600px] max-h-[95vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient header */}
        <div className="relative px-6 py-5 text-white" style={gradientStyle}>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 rounded-full p-1 text-white/80 transition hover:bg-white/20 hover:text-white"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 id="demo-modal-title" className="text-xl font-semibold">
            {successMsg ? 'Thank you!' : 'Book a Demo'}
          </h2>
          {!successMsg && (
            <p className="mt-1 text-sm text-white/90">
              Share your details and preferred slot. We will get back to you shortly.
            </p>
          )}
        </div>

        <div className="p-6">
          {successMsg ? (
            <div className="py-4 text-center">
              <div
                className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
                style={gradientStyle}
              >
                <svg className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-gray-600">{successMsg}</p>
              <button
                type="button"
                onClick={onClose}
                style={gradientStyle}
                className="mt-6 rounded-lg px-6 py-2 text-sm font-semibold text-white shadow transition hover:opacity-90"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {serverError && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {serverError}
                </div>
              )}

              <Field label="Full name" error={errors.name}>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  maxLength={200}
                  placeholder="Enter your name"
                  autoComplete="name"
                  className={inputClass(errors.name)}
                />
              </Field>

              <Field label="Phone number" error={errors.phone}>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Enter your phone number"
                  autoComplete="tel-national"
                  className={inputClass(errors.phone)}
                />
              </Field>

              <Field label="Email" error={errors.email}>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  autoComplete="email"
                  className={inputClass(errors.email)}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Preferred date" error={errors.preferred_date}>
                  <input
                    type="date"
                    name="preferred_date"
                    value={form.preferred_date}
                    onChange={handleChange}
                    min={today}
                    className={inputClass(errors.preferred_date)}
                  />
                </Field>
                <Field label="Preferred time" error={errors.preferred_time}>
                  <input
                    type="time"
                    name="preferred_time"
                    value={form.preferred_time}
                    onChange={handleChange}
                    min={minTime}
                    className={inputClass(errors.preferred_time)}
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={gradientStyle}
                className="w-full rounded-lg py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-90 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Submitting…' : 'Book Demo'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const BookDemoButton = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={gradientStyle}
        className="fixed bottom-5 left-5 z-50 flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-[#c94daa]/40"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        Book a Demo
      </button>

      {open && createPortal(<DemoModal onClose={() => setOpen(false)} />, document.body)}
    </>
  );
};

export default BookDemoButton;